/**
 * Enum định nghĩa trạng thái bật/tắt thống nhất toàn hệ thống cho các cấu hình (Setting)
 */
export enum ESettingStatus {
  TURN_ON = 1,
  TURN_OFF = 2
}

/** Trạng thái dùng cho các cài đặt có thêm lựa chọn chạy theo khung giờ. */
export enum ETimedSettingStatus {
  TURN_OFF = 1,
  ALL_DAY = 2,
  CUSTOM_TIME = 3,
}
/** File ảnh/video được lưu trong nội dung tự động trả lời. */
export interface IPageSettingAttachment {
  id?: string;
  url: string;
  uri?: string;
  type?: 'image' | 'video';
  faceAttachmentId?: string;
}
/** Cấu hình thời gian dùng chung cho tự động trả lời và trạng thái hội thoại đến. */
export interface ITimedPageSetting {
  status: ETimedSettingStatus;
  sh: number;
  sm: number;
  eh: number;
  em: number;
}
/** Cấu hình trả lời riêng bằng tin nhắn. */
export interface IAutoReplyPrivateSetting extends ITimedPageSetting {
  message: string;
  replyLivePost: ESettingStatus;
  attachments: Record<string, unknown>;
}
/** Cấu hình trả lời công khai bằng một trong nhiều mẫu bình luận. */
export interface IAutoReplyCommentSetting extends ITimedPageSetting {
  message: string;
  replyLivePost: ESettingStatus;
  attachments: IPageSettingAttachment[];
}
/**
 * Cấu hình kịch bản tự động mời lại khách hàng
 */
export interface IAutoInboxRule {
  status: ESettingStatus;
  message: string;
  attachments?: unknown[];
}


/**
 * [§4.10] Cấu hình tự động chặn/cảnh báo từ khóa nhạy cảm khi gửi tin nhắn.
 * mode: 0 = tắt · 1 = cảnh báo (hiện dialog) · 2 = chặn (không gửi)
 */
export interface IAutoBlockKeywords {
  mode: 0 | 1 | 2;
  keywords: string[];
}

export interface ICartMessengerAttachment {
  id: string;
  url: string;
  faceAttachmentId?: string;
}

export interface ICartMessengerSetting {
  sendCartMessengerCare: ESettingStatus;
  textOrderMessengerCare: string;
  settingCartMessengerCare: {
    customTemplate: ESettingStatus;
    titleButton: string;
  };
  tagsAddOrder: {
    status: ESettingStatus;
    tagCodes: string[];
  };
  attachments: ICartMessengerAttachment[];
  depotId: string | number;
}

/** Ảnh đính kèm trong nội dung chăm sóc khách hàng của Live Shopping. */
export interface ILiveSettingAttachment {
  id: string;
  url: string;
  faceAttachmentId?: string;
}

/** Cấu hình nút và ảnh đi kèm tin nhắn Live Shopping. */
export interface ILiveMessageSetting {
  customTemplate: ESettingStatus;
  titleButton: string;
  attachments?: ILiveSettingAttachment[];
}

/** Cấu hình yêu cầu chuyển khoản trước 1 phần. */
export interface ILivePartialPrepaymentSetting {
  status: ESettingStatus;
  type: 1 | 2; // 1: Số tiền cố định, 2: % giá trị đơn hàng
  amount: number | null;
  percent: number | null;
  minAmount: number | null;
  maxAmount: number | null;
}

/** Cài đặt tự động dùng chung cho Live Shopping và chốt đơn tự động. */
export interface ILiveShoppingSetting {
  syncInventory: ESettingStatus;
  mergeOrder: ESettingStatus;
  mergeOrderAfter: number;
  mergeOrderUnit: 1 | 2;
  sendConfirmOrder: ESettingStatus;
  textConfirmOrder: string;
  settingConfirmOrder: ILiveMessageSetting;
  sendOrderCare: ESettingStatus;
  textOrderCare: string;
  settingOrderCare: ILiveMessageSetting;
  tagsAddOrder: {
    status: ESettingStatus;
    tagCodes: string[];
  };
  typePaymentCheckout: 1 | 2 | 3 | 4;
  partialPrepayment: ILivePartialPrepaymentSetting;
  autoInboxAfterPrintCommentStatus: ESettingStatus;
  autoInboxAfterPrintCommentContent: string;
  autoReplyCommentAfterPrintCommentStatus: ESettingStatus;
  autoReplyCommentAfterPrintCommentContent: string;
  combinedPrintingStatus: ESettingStatus;
}

/** Cài đặt liên quan đến bình luận Livestream của một fanpage. */
export interface IPageLiveSetting {
  addTagAfterPrint: {
    status: ESettingStatus;
    tagIds: string[];
  };
  settingPrint: {
    typePrint: 'wifi' | 'bluetooth' | '';
    ipAddress: string;
    portId: string;
    paperSize: 'K58' | 'K80' | '';
  };
  createOrderDraftHavePhone: ESettingStatus;
}

/**
 * Object Setting tổng thể của Page
 */
export interface IPageSetting {
  [key: string]: unknown;
  cartMessenger?: ICartMessengerSetting;
  settingLiveShopping?: ILiveShoppingSetting;
  inbox: {
    autoReInbox: {
      [key: string]: IAutoInboxRule; // rule1, rule2... rule9
    };
    autoReInboxEcom?: Record<string, unknown>;
  };
  comments: {
    autoHide: ESettingStatus;
    autoHidePhone: ESettingStatus;
    autoUnHide: {
      status: ESettingStatus;
      at: number; // Số ngày (1-15)
    };
    autoLikeCommentReply: ESettingStatus;
    autoUnreadReplyPrivateMsg: ESettingStatus;
    autoUnreadReplyComment: ESettingStatus;
    autoReply: IAutoReplyPrivateSetting;
    autoReplyCommentWithComment: IAutoReplyCommentSetting;
    skipAllConversationFromComment: {
      status: ESettingStatus;
      excludeLiveStreamPostsStatus: ESettingStatus;
      excludePostsContainKeyWordsStatus: ESettingStatus;
      excludePostsContainKeyWords: string[];
      pullPrivatePostsStatus: ESettingStatus;
      pullPrivatePosts: string[];
    };
    autoBlockUserSpam: ESettingStatus;
    autoDelCommentKey: string[];
    live: IPageLiveSetting;
  };
  chatbot: {
    autoStop: ESettingStatus;
    autoStart: ESettingStatus;
    lastTime: number; // Phút
    updatedAt?: number;
  };
  tags: {
    showSuggestTagging?: ESettingStatus;
    typeShowSuggest?: 1 | 2;
    autoAddTag: {
      status: ESettingStatus;
      tagCodes: string[];
    };
  };
  absent: ITimedPageSetting;
  customerCare?: {
    sendPurchaseEvent?: ESettingStatus;
    sendValuePurchaseEvent?: 1 | 2;
    [key: string]: unknown;
  };
  conversation: {
    autoReadReaction: ESettingStatus;
    skipReactionFromMessage: ESettingStatus;
    skipReactionFromComment: ESettingStatus;
    skipShopeeNotificationMessage: ESettingStatus;
    autoBlockKeywords: IAutoBlockKeywords;
  };
}
